/**
 * Background catalog lookups with the model on your computer. Works through products that have
 * no notes yet, 8 at a time, and saves what the model says as AI catalog notes (keywords, an
 * icon, a readable name, taxed or not). Never overwrites notes you wrote yourself. Runs only in
 * this browser, while the app is open and LM Studio is reachable.
 */
import {
  LOOKUP_SYSTEM,
  hasLabels,
  lookupPrompt,
  lookupQueue,
  parseLookupReply,
  type LookupItem,
} from "@split/core";
import { app } from "../store.svelte.ts";
import { assistant } from "./assistant.svelte.ts";
import { chat, LlmError } from "./llm.ts";

const BATCH = 8;
const AUTO_KEY = "ai:lookup:auto";

function readAuto(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) === "1";
  } catch {
    return false;
  }
}

class CatalogLookup {
  running = $state(false);
  done = $state(0);
  total = $state(0);
  error = $state("");
  /** Look up new products automatically whenever the app is open (this device). */
  auto = $state(readAuto());
  private abort: AbortController | null = null;

  setAuto(on: boolean) {
    this.auto = on;
    try {
      if (on) localStorage.setItem(AUTO_KEY, "1");
      else localStorage.removeItem(AUTO_KEY);
    } catch {
      /* fine */
    }
    if (on) void this.start();
  }

  pending(redoAi = false): LookupItem[] {
    return lookupQueue(app.ledger.catalog, app.current.catalogNotes, redoAi);
  }

  stop() {
    this.abort?.abort();
  }

  async start(redoAi = false, outer?: AbortSignal): Promise<void> {
    if (this.running || app.role !== "owner" || !app.canEdit) return;
    const queue = this.pending(redoAi);
    if (!queue.length) return;
    if (assistant.connection.status !== "ok") await assistant.check();
    if (assistant.connection.status !== "ok") {
      this.error = assistant.connection.message || "Can't reach LM Studio.";
      return;
    }
    this.running = true;
    this.error = "";
    this.done = 0;
    this.total = queue.length;
    this.abort = new AbortController();
    outer?.addEventListener("abort", () => this.abort?.abort());
    try {
      for (let i = 0; i < queue.length; i += BATCH) {
        const batch = queue.slice(i, i + BATCH);
        const r = await chat(
          assistant.settings,
          [
            { role: "system", content: LOOKUP_SYSTEM },
            { role: "user", content: lookupPrompt(batch) },
          ],
          [],
          () => {},
          this.abort.signal,
        );
        for (const { key, note } of parseLookupReply(r.content, batch)) {
          // Someone (you) may have written a note while this was running: keep yours.
          const now = app.current.catalogNotes?.[key];
          if (now && now.source !== "ai" && hasLabels(now)) continue;
          // Keep a price you noted for price adjustments.
          const keep =
            now?.priceNowCents === undefined
              ? {}
              : { priceNowCents: now.priceNowCents, priceSeenOn: now.priceSeenOn };
          await app.do_({ type: "catalog.note", key, note: { ...note, ...keep } });
        }
        this.done = Math.min(queue.length, i + batch.length);
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        this.error =
          e instanceof LlmError ? e.message : `The lookup stopped: ${(e as Error).message}`;
      }
    } finally {
      this.running = false;
      this.abort = null;
    }
  }
}

export const catalogLookup = new CatalogLookup();
